import CmsEditableList from "../components/cms/CmsEditableList";
import CmsEditableText from "../components/cms/CmsEditableText";
import CmsEditBar from "../components/cms/CmsEditBar";
import { useCmsPage } from "../hooks/useCmsPage";

const POLICIES = {
  terms: {
    title: "Terms & Conditions",
    intro:
      "These terms outline how Bobbin USA orders, site access, and customer responsibilities are handled.",
    sections: [
      {
        heading: "Orders",
        body:
          "Product availability, pricing, and order acceptance may be updated as inventory changes. We will contact you if an item needs clarification before fulfillment.",
      },
      {
        heading: "Product Information",
        body:
          "We work to keep descriptions, images, and compatibility details accurate. Customers should confirm part fitment before ordering specialized sewing machine parts.",
      },
      {
        heading: "Site Use",
        body:
          "The website may only be used for lawful purchases and account activity. Misuse, unauthorized access, or fraudulent activity may result in order cancellation.",
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    intro:
      "This policy explains the basic information Bobbin USA may collect to process orders and respond to customer requests.",
    sections: [
      {
        heading: "Information We Collect",
        body:
          "We may collect contact details, shipping information, order details, and messages submitted through forms or customer support channels.",
      },
      {
        heading: "How We Use Information",
        body:
          "Customer information is used to process orders, provide support, improve service, and communicate about purchases or account activity.",
      },
      {
        heading: "Data Protection",
        body:
          "We limit access to customer information and use reasonable safeguards to protect order and account details.",
      },
    ],
  },
  refund: {
    title: "Refund Policy",
    intro:
      "This policy summarizes how returns, refunds, and product issues are reviewed by Bobbin USA.",
    sections: [
      {
        heading: "Refund Eligibility",
        body:
          "Refunds may be reviewed for damaged, incorrect, defective, or eligible returned items. Items should be unused unless the issue is related to product defect.",
      },
      {
        heading: "Return Review",
        body:
          "Please contact us with your order details before sending an item back. Some parts may require inspection before a refund is approved.",
      },
      {
        heading: "Processing",
        body:
          "Approved refunds are returned to the original payment method when possible. Processing time can vary by payment provider.",
      },
    ],
  },
};

function PolicyPage({ type }) {
  const policy = POLICIES[type] || POLICIES.terms;
  const cms = useCmsPage(type, {
    policy: {
      eyebrow: "Useful Links",
      title: policy.title,
      intro: policy.intro,
    },
  });
  const sections = cms.json("policy", "sections", policy.sections);

  return (
    <section className="bg-[#f8fbff] px-4 py-12 text-slate-900 sm:px-8">
      <CmsEditBar editMode={cms.editMode} pageTitle={policy.title} />
      <div className="container">
        <div className="mx-auto max-w-4xl">
          <CmsEditableText as="p" className="text-sm font-semibold uppercase tracking-[0.18em] text-[#3991D5]" editMode={cms.editMode} fieldKey="eyebrow" onSave={cms.updateField} sectionKey="policy" value={cms.text("policy", "eyebrow", "Useful Links")} />
          <CmsEditableText as="h1" className="mt-3 font-gopher text-4xl font-black uppercase leading-tight text-slate-950 sm:text-5xl" editMode={cms.editMode} fieldKey="title" onSave={cms.updateField} sectionKey="policy" value={cms.text("policy", "title", policy.title)} />
          <CmsEditableText as="p" className="mt-4 max-w-3xl font-pmesans text-base leading-7 text-slate-600 sm:text-lg" editMode={cms.editMode} fieldKey="intro" multiline onSave={cms.updateField} sectionKey="policy" value={cms.text("policy", "intro", policy.intro)} />

          <div className="mt-8 space-y-4">
            <CmsEditableList editMode={cms.editMode} fieldKey="sections" fields={["heading", "body"]} itemName="policy box" label={`${policy.title} content boxes`} onSave={cms.updateField} sectionKey="policy" value={sections} />
            {sections.map((section) => (
              <article
                className="rounded-lg border border-[#3991D5]/10 bg-white p-5 shadow-sm"
                key={section.heading}
              >
                <h2 className="font-gopher text-xl font-black uppercase text-slate-950">
                  {section.heading}
                </h2>
                <p className="mt-2 font-pmesans text-sm leading-6 text-slate-600 sm:text-base">
                  {section.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default PolicyPage;
